import React, { useState, useEffect } from 'react';
import { X, Camera } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import VehiclePhotosModal from './VehiclePhotosModal';

const VehicleModal = ({ vehicle, onClose }) => {
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    plateNumber: '',
    customerId: '',
    brandName: '',
    modelName: '',
    brandId: '',
    year: '',
    color: '',
    vin: '',
    engine: '',
    transmission: '',
    fuelType: '',
    mileage: '',
    status: 'ACTIVE',
    notes: '',
    insuranceExpiry: '',
    registrationExpiry: ''
  });
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState({
    photos: []
  });
  const [previewUrls, setPreviewUrls] = useState({});
  const [showPhotosModal, setShowPhotosModal] = useState(false);

  useEffect(() => {
    // Cleanup blob URLs on unmount
    return () => {
      files.photos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, [files.photos]);

  const createPreviewUrl = (file, fieldName) => {
    // Revoke previous URL if exists
    if (previewUrls[fieldName] && previewUrls[fieldName].startsWith('blob:')) {
      URL.revokeObjectURL(previewUrls[fieldName]);
    }
    
    const url = URL.createObjectURL(file);
    setPreviewUrls(prev => ({ ...prev, [fieldName]: url }));
    return url;
  };

  useEffect(() => {
    if (vehicle?.type === 'brand' || vehicle?.type === 'model') {
      fetchBrands();
      fetchModels();
    } else {
      fetchCustomers();
      fetchBrands();
      fetchModels();
    }
    
    if (vehicle) {
      if (vehicle.type === 'brand') {
        setFormData({
          brandName: vehicle.name || '',
          brandId: vehicle.id || ''
        });
      } else if (vehicle.type === 'model') {
        setFormData({
          modelName: vehicle.name || '',
          brandId: vehicle.brandId || ''
        });
      } else {
        setFormData({
          make: vehicle.make || '',
          model: vehicle.model || '',
          plateNumber: vehicle.plateNumber || '',
          customerId: vehicle.customerId || '',
          year: vehicle.year || '',
          color: vehicle.color || '',
          vin: vehicle.vin || '',
          engine: vehicle.engine || '',
          transmission: vehicle.transmission || '',
          fuelType: vehicle.fuelType || '',
          mileage: vehicle.mileage || '',
          status: vehicle.status || 'ACTIVE',
          notes: vehicle.notes || '',
          insuranceExpiry: vehicle.insuranceExpiry ? vehicle.insuranceExpiry.split('T')[0] : '',
          registrationExpiry: vehicle.registrationExpiry ? vehicle.registrationExpiry.split('T')[0] : ''
        });
      }
    }
  }, [vehicle]);

  useEffect(() => {
    if (formData.make) {
      const selectedBrand = brands.find(b => b.name === formData.make);
      if (selectedBrand) {
        const brandModels = models.filter(m => m.brandId === selectedBrand.id);
        setFilteredModels(brandModels);
      } else {
        setFilteredModels([]);
      }
    } else {
      setFilteredModels([]);
      if (!vehicle) {
        setFormData(prev => ({ ...prev, model: '' }));
      }
    }
  }, [formData.make, brands, models, vehicle]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get('/api/vehicles/brands');
      setBrands(response.data || []);
    } catch (error) {
    }
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get('/api/vehicles/models');
      setModels(response.data || []);
    } catch (error) {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitFormData = new FormData();
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      if (vehicle?.type === 'brand') {
        submitFormData.append('name', formData.brandName);
        if (files.logoUrl) {
          submitFormData.append('logoUrl', files.logoUrl);
        }
        
        if (vehicle.id) {
          await axios.put(`/api/vehicles/brands/${vehicle.id}`, submitFormData, config);
          toast.success('Brand updated successfully');
        } else {
          await axios.post('/api/vehicles/brands', submitFormData, config);
          toast.success('Brand created successfully');
        }
      } else if (vehicle?.type === 'model') {
        submitFormData.append('name', formData.modelName);
        submitFormData.append('brandId', formData.brandId);
        if (files.photoUrl) {
          submitFormData.append('photoUrl', files.photoUrl);
        }
        
        if (vehicle.id) {
          await axios.put(`/api/vehicles/models/${vehicle.id}`, submitFormData, config);
          toast.success('Model updated successfully');
        } else {
          await axios.post('/api/vehicles/models', submitFormData, config);
          toast.success('Model created successfully');
        }
      } else {
        // Regular vehicle
        submitFormData.append('make', formData.make);
        submitFormData.append('model', formData.model);
        submitFormData.append('plateNumber', formData.plateNumber || '');
        submitFormData.append('year', formData.year || '');
        submitFormData.append('color', formData.color || '');
        submitFormData.append('vin', formData.vin || '');
        submitFormData.append('engine', formData.engine || '');
        submitFormData.append('transmission', formData.transmission || '');
        submitFormData.append('fuelType', formData.fuelType || '');
        submitFormData.append('mileage', formData.mileage || '');
        submitFormData.append('status', formData.status);
        submitFormData.append('notes', formData.notes || '');
        submitFormData.append('insuranceExpiry', formData.insuranceExpiry || '');
        submitFormData.append('registrationExpiry', formData.registrationExpiry || '');
        if (formData.customerId) {
          submitFormData.append('customerId', formData.customerId);
        }
        files.photos.forEach((photo, index) => {
          submitFormData.append('photos', photo.file);
        });
        
        if (vehicle?.id) {
          await axios.put(`/api/vehicles/${vehicle.id}`, submitFormData, config);
          toast.success('Vehicle updated successfully');
        } else {
          await axios.post('/api/vehicles', submitFormData, config);
          toast.success('Vehicle added successfully');
        }
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    
    selectedFiles.forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }
      
      if (file.size > 300 * 1024) {
        toast.error(`${file.name} is too large (max 300KB)`);
        return;
      }
      
      const preview = URL.createObjectURL(file);
      validFiles.push({ file, preview });
    });
    
    setFiles(prev => ({
      ...prev,
      photos: [...prev.photos, ...validFiles]
    }));
  };
  
  const handleSingleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      if (file.size > 300 * 1024) {
        toast.error('File size must be less than 300KB');
        return;
      }
      
      setFiles(prev => ({ ...prev, [fieldName]: file }));
      createPreviewUrl(file, fieldName);
    }
  };
  
  const removePhoto = (index) => {
    setFiles(prev => {
      const newPhotos = [...prev.photos];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return { ...prev, photos: newPhotos };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {vehicle?.type === 'brand' ? (vehicle.id ? 'Edit Brand' : 'Add Brand') :
             vehicle?.type === 'model' ? (vehicle.id ? 'Edit Model' : 'Add Model') :
             (vehicle?.id ? 'Edit Vehicle' : 'Add New Vehicle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {vehicle?.type === 'brand' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Name *
                </label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Toyota, BMW, Mercedes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Logo (Optional)
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => handleSingleFileChange(e, 'logoUrl')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {(previewUrls.logoUrl || vehicle?.logoUrl) && (
                  <div className="mt-2 relative">
                    <img
                      src={previewUrls.logoUrl || `http://localhost:5000${vehicle.logoUrl}`}
                      alt="Brand logo preview"
                      className="w-full h-32 object-contain rounded-md border bg-gray-50"
                    />
                    {previewUrls.logoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setFiles(prev => ({ ...prev, logoUrl: null }));
                          setPreviewUrls(prev => ({ ...prev, logoUrl: null }));
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : vehicle?.type === 'model' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand *
                </label>
                <select
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Name *
                </label>
                <input
                  type="text"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Camry, X5, C-Class"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Photo (Optional)
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => handleSingleFileChange(e, 'photoUrl')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {(previewUrls.photoUrl || vehicle?.photoUrl) && (
                  <div className="mt-2 relative">
                    <img
                      src={previewUrls.photoUrl || `http://localhost:5000${vehicle.photoUrl}`}
                      alt="Model photo preview"
                      className="w-full h-32 object-cover rounded-md border"
                    />
                    {previewUrls.photoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setFiles(prev => ({ ...prev, photoUrl: null }));
                          setPreviewUrls(prev => ({ ...prev, photoUrl: null }));
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make *
                </label>
                <select
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Make</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.name}>{brand.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model *
                </label>
                <select
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  disabled={!formData.make}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select Model</option>
                  {filteredModels.map(model => (
                    <option key={model.id} value={model.name}>{model.name}</option>
                  ))}
                </select>
                {!formData.make && (
                  <p className="text-sm text-gray-500 mt-1">Please select a make first</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plate Number
                </label>
                <input
                  type="text"
                  name="plateNumber"
                  value={formData.plateNumber}
                  onChange={handleChange}
                  placeholder="e.g., ABC-123"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    placeholder="e.g., 2023"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="e.g., White, Black, Silver"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VIN Number
                </label>
                <input
                  type="text"
                  name="vin"
                  value={formData.vin}
                  onChange={handleChange}
                  maxLength="17"
                  placeholder="17-character VIN"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Engine
                  </label>
                  <input
                    type="text"
                    name="engine"
                    value={formData.engine}
                    onChange={handleChange}
                    placeholder="e.g., 2.0L Turbo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transmission
                  </label>
                  <select
                    name="transmission"
                    value={formData.transmission}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="Manual">Manual</option>
                    <option value="Automatic">Automatic</option>
                    <option value="CVT">CVT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fuel Type
                  </label>
                  <select
                    name="fuelType"
                    value={formData.fuelType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="Gasoline">Gasoline</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Electric">Electric</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mileage (km)
                  </label>
                  <input
                    type="number"
                    name="mileage"
                    value={formData.mileage}
                    onChange={handleChange}
                    min="0"
                    placeholder="e.g., 50000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="MAINTENANCE">Under Maintenance</option>
                    <option value="SOLD">Sold</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insurance Expiry
                  </label>
                  <input
                    type="date"
                    name="insuranceExpiry"
                    value={formData.insuranceExpiry}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Expiry
                  </label>
                  <input
                    type="date"
                    name="registrationExpiry"
                    value={formData.registrationExpiry}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer
                </label>
                <select
                  name="customerId"
                  value={formData.customerId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Customer (Optional)</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.contact && `(${customer.contact})`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Additional notes about the vehicle..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Vehicle Photos (Optional)
                  </label>
                  {vehicle?.id && (
                    <button
                      type="button"
                      onClick={() => setShowPhotosModal(true)}
                      className="flex items-center px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      View Photos
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMultipleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {files.photos.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {files.photos.map((photo, index) => (
                      <div key={index} className="relative">
                        <img
                          src={photo.preview}
                          alt={`Vehicle photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-md border"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}



          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 
               (vehicle?.type === 'brand' ? (vehicle.id ? 'Update Brand' : 'Add Brand') :
                vehicle?.type === 'model' ? (vehicle.id ? 'Update Model' : 'Add Model') :
                (vehicle?.id ? 'Update Vehicle' : 'Add Vehicle'))}
            </button>
          </div>
        </form>
      </div>
      
      {showPhotosModal && vehicle?.id && (
        <VehiclePhotosModal
          vehicle={vehicle}
          onClose={() => setShowPhotosModal(false)}
        />
      )}
    </div>
  );
};

export default VehicleModal;
